import {
  DescriptionList,
  DescriptionListDetails,
  DescriptionListTerm,
  InlineStack,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Tag,
} from '@customerio/pluma-components/react';
import { formatLongTimestamp } from '@/utils/calendar-timestamps';
import type { DeliveryDetail } from '@/services/db/delivery/entity';
import { buildInboxConfig } from '../utils/inbox-config';

type InboxConfigProps = { detail: DeliveryDetail };

/**
 * The Inbox Config panel — priority, expiration, type, and topics — ported from
 * the Ember `cio-delivery-detail/inbox-config` component. As with `InAppConfig`,
 * the Ember aside isn't ported yet, so this renders as a panel in the main flow.
 */
export function InboxConfig({ detail }: InboxConfigProps) {
  const config = buildInboxConfig(detail);

  return (
    <Panel data-test-inbox-config>
      <PanelHeader>
        <PanelTitle>Inbox Config</PanelTitle>
      </PanelHeader>

      <PanelSection>
        <DescriptionList layout="horizontal">
          <DescriptionListTerm text="product-p-secondary" color="subtle">
            Priority
          </DescriptionListTerm>
          <DescriptionListDetails data-test-inbox-priority>{config.priority}</DescriptionListDetails>

          {config.expiration != null ? (
            <>
              <DescriptionListTerm text="product-p-secondary" color="subtle">
                Expiration
              </DescriptionListTerm>
              <DescriptionListDetails data-test-inbox-expiration>
                {formatLongTimestamp(config.expiration)}
              </DescriptionListDetails>
            </>
          ) : null}

          {config.type != null ? (
            <>
              <DescriptionListTerm text="product-p-secondary" color="subtle">
                Type
              </DescriptionListTerm>
              <DescriptionListDetails data-test-inbox-type>{config.type}</DescriptionListDetails>
            </>
          ) : null}

          {config.topics.length > 0 ? (
            <>
              <DescriptionListTerm text="product-p-secondary" color="subtle">
                Topics
              </DescriptionListTerm>
              <DescriptionListDetails data-test-inbox-topics>
                <InlineStack gap="100" wrap>
                  {config.topics.map((topic) => (
                    <Tag key={topic} isCompact>
                      {topic}
                    </Tag>
                  ))}
                </InlineStack>
              </DescriptionListDetails>
            </>
          ) : null}
        </DescriptionList>
      </PanelSection>
    </Panel>
  );
}
